<template>
  <div class="ren-decoration-cp ren-decoration-cp-products">
    <header>
      <strong>{{ title }}</strong>
      <span v-if="products.length">{{ products.length }} 件</span>
    </header>
    <div v-if="products.length === 0" class="ren-decoration-cp-products__empty">暂无商品</div>
    <div v-else class="ren-decoration-cp-products__list">
      <article v-for="(product, index) in products" :key="itemKey(product, index)" class="ren-decoration-cp-product-card">
        <img
          v-if="coverUrl(product)"
          class="ren-decoration-cp-product-card__image"
          :src="coverUrl(product)"
          :alt="itemTitle(product)"
        />
        <div v-else class="ren-decoration-cp-product-card__art" :style="{ backgroundColor: artColor }">
          {{ itemTitle(product).slice(0, 1) }}
        </div>
        <div class="ren-decoration-cp-product-card__info">
          <strong>{{ itemTitle(product) }}</strong>
          <span v-if="itemMeta(product)">{{ itemMeta(product) }}</span>
          <small v-if="itemSubtitle(product)">{{ itemSubtitle(product) }}</small>
          <div class="ren-decoration-cp-product-card__bottom">
            <b>¥{{ formatMoney(itemPrice(product)) }}</b>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  formatMoney,
  numberValue,
  previewBoundString,
  previewRecordsFor,
  recordList,
  stringValue
} from '../../../previews/preview-helpers'
import { widgetMediaUrl } from '../../media'
import type { WidgetPreviewProps } from '../../types'

const props = defineProps<WidgetPreviewProps>()

const title = computed(() => stringValue(props.component.props?.title, '推荐商品'))
const artColor = computed(
  () => props.themeTokens?.primaryColor || props.themeColor || 'var(--ren-theme-primary)'
)
const products = computed(() => {
  const limit = numberValue(props.component.props?.limit, 12)
  const records = previewRecordsFor(
    props.component,
    props.componentData,
    recordList(props.component.props?.items)
  )
  const size = Number.isFinite(limit) && limit > 0 ? Math.min(12, Math.round(limit)) : 12
  return records.slice(0, size)
})

function itemTitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.title ?? item.name, '商品'),
    item
  )
}

function itemMeta(item: Record<string, unknown>) {
  return stringValue(item.merchantName ?? item.shopName)
}

function itemSubtitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.subtitle',
    stringValue(item.subtitle ?? item.categoryName),
    item
  )
}

function itemPrice(item: Record<string, unknown>) {
  return numberValue(item.minSalePrice ?? item.price, 0)
}

function coverUrl(item: Record<string, unknown>) {
  return widgetMediaUrl(
    previewBoundString(
      props.component,
      props.componentData,
      'item.imageUrl',
      stringValue(item.coverImageUrl ?? item.imageUrl ?? item.image),
      item
    )
  )
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code, `${itemTitle(item)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-products__empty {
  min-height: 96px;
  display: grid;
  place-items: center;
  color: var(--ren-theme-muted);
  font-size: 12px;
  background: var(--ren-theme-background);
}
</style>
